/**
 * The calibration table.
 *
 * This is the screen that decides whether the whole platform is honest, so it
 * shows, for every signal and horizon: the hit rate, its 95% interval, the
 * unconditional baseline over the same bars, the excess, the independent
 * sample count after de-overlapping, how many time folds it beat the baseline
 * in, and the multiple-testing-corrected q-value. A bare percentage would be
 * the one number here that means nothing on its own.
 */
import {
  type CalibrationResult, VERDICT_COPY, formatPct, formatSigned,
} from "@tt/shared";
import { useMemo, useState } from "react";

import { useAction, useAsync } from "../hooks/useApi";
import { api } from "../lib/api";
import { ConfidenceBadge, IntervalBar, VerdictBadge } from "../components/Verdict";

type SortKey = "excess" | "hit_rate" | "n" | "q";

export function SignalsPanel({
  symbols, timeframe, onPickSymbol,
}: {
  symbols: string[];
  timeframe: string;
  onPickSymbol: (s: string) => void;
}) {
  const [tab, setTab] = useState<"fired" | "calibration" | "catalogue">("fired");
  const [sortKey, setSortKey] = useState<SortKey>("excess");
  const [onlyMeasured, setOnlyMeasured] = useState(false);
  const [horizonFilter, setHorizonFilter] = useState<number | null>(null);

  const definitions = useAsync((s) => api.signalDefinitions(s), []);
  const fired = useAsync(
    (s) => api.latestSignals({ symbols, timeframe, within_bars: 5 }, s),
    [symbols.join(","), timeframe],
    symbols.length > 0,
  );
  const calibration = useAction((body: Parameters<typeof api.calibrate>[0]) => api.calibrate(body));

  const rows = useMemo(() => {
    const all = calibration.data?.results ?? [];
    let out = all;
    if (onlyMeasured) out = out.filter((r) => r.verdict.verdict === "measured-edge");
    if (horizonFilter !== null) out = out.filter((r) => r.horizon_bars === horizonFilter);
    const key = (r: CalibrationResult) =>
      sortKey === "excess" ? -r.excess_vs_baseline
      : sortKey === "hit_rate" ? -r.hit_rate
      : sortKey === "n" ? -r.n_independent
      : (r.q_value ?? 1);
    return [...out].sort((a, b) => key(a) - key(b));
  }, [calibration.data, onlyMeasured, horizonFilter, sortKey]);

  const horizons = calibration.data?.horizons ?? [];

  return (
    <div className="panel">
      <div className="row" style={{ marginBottom: 10 }}>
        <div className="tf-group">
          {(["fired", "calibration", "catalogue"] as const).map((t) => (
            <button
              key={t}
              className={`btn small ${tab === t ? "active" : ""}`}
              onClick={() => setTab(t)}
            >
              {t === "fired" ? "Firing now" : t === "calibration" ? "Track record" : "Catalogue"}
            </button>
          ))}
        </div>
        <span className="spacer" />
        {tab === "calibration" && (
          <>
            <button
              className="btn primary"
              disabled={calibration.loading || symbols.length < 3}
              onClick={() =>
                void calibration.run({
                  symbols, timeframe, horizons: [1, 5, 10, 20], lookback_bars: 1500,
                })
              }
              title={
                symbols.length < 3
                  ? "Add at least 3 symbols to the watchlist; one symbol cannot support a hit rate"
                  : "Walk-forward calibration over the watchlist"
              }
            >
              {calibration.loading ? <span className="spinner" /> : null} Calibrate {symbols.length} symbols
            </button>
            {calibration.data && (
              <span className="small faint mono">
                {Math.round((calibration.data.timing_ms.total ?? 0) / 1000)}s
              </span>
            )}
          </>
        )}
      </div>

      {tab === "fired" && (
        <FiredList
          state={fired}
          onPickSymbol={onPickSymbol}
          symbolCount={symbols.length}
        />
      )}

      {tab === "catalogue" && (
        <>
          <div className="notebox">{definitions.data?.note}</div>
          <div className="table-scroll">
            <table className="grid">
              <thead>
                <tr>
                  <th>Signal</th><th>Family</th><th>Side</th>
                  <th>What it looks for</th><th>Why anyone believes it</th>
                </tr>
              </thead>
              <tbody>
                {(definitions.data?.signals ?? []).map((d) => (
                  <tr key={d.id}>
                    <td><strong>{d.label}</strong><div className="faint small mono">{d.id}</div></td>
                    <td className="small">{d.family}</td>
                    <td className={d.direction > 0 ? "up" : "down"}>
                      {d.direction > 0 ? "long" : "short"}
                    </td>
                    <td className="small muted">{d.description}</td>
                    <td className="small muted">{d.rationale}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {tab === "calibration" && (
        <>
          {calibration.error && <div className="err">{calibration.error.message}</div>}
          {!calibration.data && !calibration.loading && (
            <div className="notebox">
              Calibration measures every built-in signal against real history on your
              watchlist: forward returns entered at the next bar's open, overlapping
              observations removed, compared against the unconditional baseline over
              the same bars, then corrected for the number of signal/horizon pairs
              tested. It takes a minute or two for 30 symbols.
            </div>
          )}
          {calibration.loading && (
            <div className="hint">
              <span className="spinner" /> Loading history, evaluating {symbols.length} symbols,
              then calibrating. This is real work, not a progress bar.
            </div>
          )}
          {calibration.data && (
            <>
              <div className="row" style={{ marginBottom: 8 }}>
                <label className="small">
                  <input
                    type="checkbox"
                    checked={onlyMeasured}
                    onChange={(e) => setOnlyMeasured(e.target.checked)}
                  />{" "}
                  only measured edges
                </label>
                <select
                  className="select"
                  value={horizonFilter === null ? "" : String(horizonFilter)}
                  onChange={(e) => setHorizonFilter(e.target.value ? Number(e.target.value) : null)}
                >
                  <option value="">all horizons</option>
                  {horizons.map((h) => <option key={h} value={h}>{h}-bar</option>)}
                </select>
                <select
                  className="select"
                  value={sortKey}
                  onChange={(e) => setSortKey(e.target.value as SortKey)}
                >
                  <option value="excess">sort by excess</option>
                  <option value="hit_rate">sort by hit rate</option>
                  <option value="n">sort by sample size</option>
                  <option value="q">sort by q-value</option>
                </select>
                <span className="spacer" />
                <span className="small faint">
                  {calibration.data.window.start?.slice(0, 10)} →{" "}
                  {calibration.data.window.end?.slice(0, 10)} ·{" "}
                  {calibration.data.universe.length} symbols
                </span>
              </div>

              <CalibrationSummary results={calibration.data.results} />

              <div className="table-scroll">
                <table className="grid">
                  <thead>
                    <tr>
                      <th>Signal</th>
                      <th>Horizon</th>
                      <th>Verdict</th>
                      <th>Hit rate</th>
                      <th>vs baseline</th>
                      <th>Interval</th>
                      <th>Sample</th>
                      <th>Folds</th>
                      <th>q</th>
                      <th>Avg ret</th>
                      <th>MFE / MAE</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={`${r.signal_id}-${r.horizon_bars}`}>
                        <td>
                          <strong>{r.label ?? r.signal_id}</strong>
                          {r.notes.length > 0 && (
                            <div className="faint small" style={{ maxWidth: 380 }}>
                              {r.notes.join(" ")}
                            </div>
                          )}
                        </td>
                        <td className="num">{r.horizon_bars}d</td>
                        <td><VerdictBadge detail={r.verdict} /></td>
                        <td className="num">{formatPct(r.hit_rate)}</td>
                        <td className={`num ${r.excess_vs_baseline > 0 ? "up" : "down"}`}>
                          {formatPct(r.excess_vs_baseline)}
                          <div className="faint small">
                            base {formatPct(r.baseline_hit_rate)}
                          </div>
                        </td>
                        <td>
                          <IntervalBar
                            low={r.ci_low} high={r.ci_high} point={r.hit_rate}
                            baseline={r.baseline_hit_rate}
                          />
                        </td>
                        <td className="num">
                          <ConfidenceBadge samples={r.n_independent} confidence={r.confidence} />
                          {r.n > r.n_independent && (
                            <div
                              className="faint small"
                              title={`${r.n} raw firings collapsed to ${r.n_independent} independent observations`}
                            >
                              of {r.n}
                            </div>
                          )}
                        </td>
                        <td className="num" title="Time folds where the signal beat the baseline">
                          {r.folds_positive}/{r.folds_total}
                        </td>
                        <td className="num">{r.q_value === null ? "—" : r.q_value.toFixed(3)}</td>
                        <td className={`num ${(r.avg_return ?? 0) > 0 ? "up" : "down"}`}>
                          {formatPct(r.avg_return, 2)}
                        </td>
                        <td className="num faint small">
                          {formatPct(r.avg_mfe, 1)} / {formatPct(r.avg_mae, 1)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="notebox">{calibration.data.caveat}</div>
              <div className="notebox">
                <strong>Reading this table</strong>
                <ul className="tight">
                  {(Object.keys(VERDICT_COPY) as (keyof typeof VERDICT_COPY)[]).map((v) => (
                    <li key={v}>
                      <span className={`badge ${VERDICT_COPY[v].tone}`}>{VERDICT_COPY[v].label}</span>{" "}
                      {VERDICT_COPY[v].blurb}
                    </li>
                  ))}
                  <li>
                    <strong>Sample</strong> counts only non-overlapping observations. A
                    20-bar signal firing on consecutive days is nearly the same trade
                    each time, so the raw count is shown beneath as "of N".
                  </li>
                  <li>
                    <strong>Interval</strong> is the Wilson 95% band; the thin grey mark
                    is the baseline. When the band straddles the baseline, the signal has
                    not demonstrated anything.
                  </li>
                  <li>
                    <strong>q</strong> is the p-value after Benjamini–Hochberg correction
                    across every signal and horizon in the run. The best of 76 tries does
                    not have the p-value it reports.
                  </li>
                </ul>
              </div>
              {Object.keys(calibration.data.skipped).length > 0 && (
                <div className="warnbox">
                  Skipped {Object.keys(calibration.data.skipped).length} symbol(s):{" "}
                  {Object.entries(calibration.data.skipped)
                    .map(([s, why]) => `${s} (${why})`)
                    .join("; ")}
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}

function CalibrationSummary({ results }: { results: CalibrationResult[] }) {
  const measured = results.filter((r) => r.verdict.verdict === "measured-edge");
  const untested = results.filter((r) => r.verdict.verdict === "untested");
  return (
    <div className="kv" style={{ marginBottom: 10 }}>
      <div className="kv-cell">
        <div className="k">signal / horizon pairs</div>
        <div className="v">{results.length}</div>
      </div>
      <div className="kv-cell">
        <div className="k">measured edges</div>
        <div className="v" style={{ color: measured.length ? "var(--tone-good)" : "var(--text-dim)" }}>
          {measured.length}
        </div>
      </div>
      <div className="kv-cell">
        <div className="k">too few samples</div>
        <div className="v">{untested.length}</div>
      </div>
      <div className="kv-cell">
        <div className="k">best excess over baseline</div>
        <div className="v">
          {results.length
            ? formatPct(Math.max(...results.map((r) => r.excess_vs_baseline)))
            : "—"}
        </div>
      </div>
    </div>
  );
}

function FiredList({
  state, onPickSymbol, symbolCount,
}: {
  state: ReturnType<typeof useAsync<Awaited<ReturnType<typeof api.latestSignals>>>>;
  onPickSymbol: (s: string) => void;
  symbolCount: number;
}) {
  if (symbolCount === 0) return <div className="empty">Add symbols to your watchlist first.</div>;
  if (state.loading) {
    return (
      <div className="hint">
        <span className="spinner" /> Evaluating {symbolCount} symbols and attaching each
        signal's measured track record…
      </div>
    );
  }
  if (state.error) return <div className="err">{state.error.message}</div>;
  const signals = state.data?.signals ?? [];
  if (signals.length === 0) {
    return <div className="empty">{state.data?.note ?? "Nothing fired recently."}</div>;
  }
  return (
    <>
      <div className="notebox">{state.data?.note}</div>
      <div className="table-scroll">
        <table className="grid">
          <thead>
            <tr>
              <th>Symbol</th><th>Signal</th><th>Side</th><th>When</th><th>Price</th>
              <th>5-bar record</th><th>10-bar record</th><th>20-bar record</th>
            </tr>
          </thead>
          <tbody>
            {signals.map((s, i) => (
              <tr key={`${s.symbol}-${s.signal_id}-${i}`}>
                <td>
                  <button
                    className="btn small"
                    onClick={() => onPickSymbol(s.symbol)}
                    style={{ fontFamily: "var(--mono)", fontWeight: 600 }}
                  >
                    {s.symbol}
                  </button>
                </td>
                <td>
                  <strong>{s.label}</strong>
                  <div className="faint small" style={{ maxWidth: 320 }}>{s.description}</div>
                </td>
                <td className={s.direction > 0 ? "up" : "down"}>
                  {s.direction > 0 ? "long" : "short"}
                </td>
                <td className="small nowrap">
                  {s.bars_ago === 0 ? "latest bar" : `${s.bars_ago} bars ago`}
                </td>
                <td className="num">{s.price.toFixed(2)}</td>
                {[5, 10, 20].map((h) => {
                  const rec = s.track_record[String(h)] ?? s.track_record[h as unknown as string];
                  return (
                    <td key={h}>
                      {rec ? (
                        <>
                          <VerdictBadge detail={rec} />
                          <div className="faint small mono">
                            {formatPct(rec.hit_rate)} vs {formatPct(rec.baseline)}{" "}
                            ({formatSigned((rec.excess ?? 0) * 100, 1)}pp, n={rec.samples})
                          </div>
                        </>
                      ) : (
                        <span className="faint small">not calibrated</span>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
